import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui';

export interface LogoutAllDevicesModalProps {
    open: boolean;
    sessionCount: number;
    pending: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

function LogoutIcon() {
    return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
            <path d="M15 17l5-5-5-5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20 12H9M13 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7" strokeLinecap="round" />
        </svg>
    );
}

/**
 * "Đăng xuất mọi thiết bị" confirm — frame 82, on the Phiên tab.
 *
 * EAA-1820: a real centered `Modal` rather than the kit's `ConfirmButton`
 * (antd `Popconfirm`, an inline popover) — frame 82's dialog carries an icon,
 * a two-tone warning card and its own footer note, which `Popconfirm`'s
 * single-line description prop can't lay out. `ProfilePage.tsx` still runs
 * `endAllSessions` (`DELETE /me/sessions`) then the local `logout()`
 * afterwards, same as before this ticket — this component only owns the
 * confirmation UI, not the mutation.
 */
export function LogoutAllDevicesModal({
    open,
    sessionCount,
    pending,
    onClose,
    onConfirm,
}: LogoutAllDevicesModalProps) {
    const { t } = useTranslation();

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={540}
            closable={false}
            maskClosable={!pending}
            title={
                <span className="portal-profile-logout-modal-head">
                    <span className="portal-profile-logout-modal-icon" aria-hidden="true">
                        <LogoutIcon />
                    </span>
                    <span className="portal-profile-logout-modal-head-text">
                        <span className="portal-profile-logout-modal-title">
                            {t('profile.logoutAllModal.title', { count: sessionCount })}
                        </span>
                        <span className="portal-profile-logout-modal-desc">
                            {t('profile.logoutAllModal.description')}
                        </span>
                    </span>
                </span>
            }
            footer={
                <div className="portal-profile-logout-modal-footer">
                    <span className="portal-profile-logout-modal-footnote">
                        {t('profile.logoutAllModal.footerNote')}
                    </span>
                    <button type="button" className="portal-btn" onClick={onClose} disabled={pending}>
                        {t('profile.logoutAllModal.cancel')}
                    </button>
                    <button
                        type="button"
                        className="portal-btn portal-btn-danger"
                        onClick={onConfirm}
                        disabled={pending}
                    >
                        {pending ? t('profile.loggingOutAll') : t('profile.logoutAll')}
                    </button>
                </div>
            }
        >
            <div className="portal-profile-logout-modal-body">
                <div className="portal-profile-logout-modal-warning">
                    <span className="portal-profile-logout-modal-warning-title">
                        {t('profile.logoutAllModal.warningTitle')}
                    </span>
                    <span>{t('profile.logoutAllModal.warningDescription')}</span>
                </div>
                <span className="portal-profile-logout-modal-hint">
                    {t('profile.logoutAllModal.keepThisDeviceNotePrefix')}
                    <strong>{t('profile.logoutAllModal.keepThisDeviceNoteBold')}</strong>
                    {t('profile.logoutAllModal.keepThisDeviceNoteSuffix')}
                </span>
            </div>
        </Modal>
    );
}

export default LogoutAllDevicesModal;
